// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter 
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Trophy, AlertTriangle, CheckCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { 
  isPoolPlayComplete, 
  calculatePoolStandings, 
  finalizePoolPlayAndPopulatePlayoffs 
} from '@/utils/poolPlayUtils';

interface FinalizePoolPlayButtonProps {
  tournamentId: string;
  teamsAdvancingPerPool?: number;
  onFinalized?: () => void;
}

export const FinalizePoolPlayButton: React.FC<FinalizePoolPlayButtonProps> = ({
  tournamentId,
  teamsAdvancingPerPool = 2,
  onFinalized,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [poolStatus, setPoolStatus] = useState<{ complete: boolean; completed: number; total: number } | null>(null);
  const [standings, setStandings] = useState<Map<string, any[]> | null>(null);
  const [finalizing, setFinalizing] = useState(false);

  const loadPoolStatus = async () => {
    setLoading(true);
    try {
      const status = await isPoolPlayComplete(tournamentId);
      setPoolStatus(status);
      
      if (status.total > 0) {
        const standingsData = await calculatePoolStandings(tournamentId);
        setStandings(standingsData);
      }
    } catch (error) {
      console.error('Error loading pool status:', error);
      toast.error('Failed to load pool status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadPoolStatus();
    }
  }, [isOpen, tournamentId]);

  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      const result = await finalizePoolPlayAndPopulatePlayoffs(tournamentId, teamsAdvancingPerPool);
      
      if (result.success) {
        toast.success(result.message);
        setIsOpen(false);
        onFinalized?.();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Error finalizing pool play:', error);
      toast.error('Failed to finalize pool play');
    } finally {
      setFinalizing(false);
    }
  };

  const getPoolLetter = (poolId: string) => {
    const match = poolId.match(/pool_([A-Z]|\d+)/i);
    return match ? match[1].toUpperCase() : poolId;
  };

  return (
    <>
      <Button 
        variant="default" 
        onClick={() => setIsOpen(true)}
        className="gap-2"
      >
        <Trophy className="h-4 w-4" />
        Finalize Pool Play
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              Finalize Pool Play & Generate Playoffs
            </DialogTitle>
            <DialogDescription>
              Review pool standings and advance top teams to the playoff bracket.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : poolStatus?.total === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertTriangle className="h-12 w-12 mx-auto mb-4" />
              <p>No pool play matches found for this tournament.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Pool Status */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Pool Play Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    {poolStatus?.complete ? (
                      <Badge variant="default" className="bg-green-500">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Complete
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        In Progress
                      </Badge>
                    )}
                    <span className="text-sm text-muted-foreground">
                      {poolStatus?.completed}/{poolStatus?.total} matches completed
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Pool Standings */}
              {standings && standings.size > 0 && (
                <div className="space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    Pool Standings
                    <span className="text-sm font-normal text-muted-foreground">
                      (Top {teamsAdvancingPerPool} from each pool advance)
                    </span>
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Array.from(standings.entries()).map(([poolId, teams]) => (
                      <Card key={poolId}>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">Pool {getPoolLetter(poolId)}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-muted-foreground">
                                <th className="text-left py-1">#</th>
                                <th className="text-left py-1">Team</th>
                                <th className="text-center py-1">W</th>
                                <th className="text-center py-1">L</th>
                                <th className="text-center py-1">+/-</th>
                              </tr>
                            </thead>
                            <tbody>
                              {teams.map((team, idx) => (
                                <tr 
                                  key={team.teamId} 
                                  className={idx < teamsAdvancingPerPool ? 'bg-green-50 dark:bg-green-950/20' : ''}
                                >
                                  <td className="py-1">
                                    <div className="flex items-center gap-1">
                                      {idx + 1}
                                      {idx < teamsAdvancingPerPool && (
                                        <ArrowRight className="h-3 w-3 text-green-600" />
                                      )}
                                    </div>
                                  </td>
                                  <td className="py-1 font-medium truncate max-w-[100px]" title={team.teamName}>
                                    {team.teamName}
                                  </td>
                                  <td className="text-center py-1">{team.wins}</td>
                                  <td className="text-center py-1">{team.losses}</td>
                                  <td className="text-center py-1">
                                    <span className={team.pointsDiff >= 0 ? 'text-green-600' : 'text-red-600'}>
                                      {team.pointsDiff > 0 ? '+' : ''}{team.pointsDiff}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Warning if incomplete */}
              {!poolStatus?.complete && (
                <div className="bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-yellow-800 dark:text-yellow-200">
                        Pool Play Not Complete
                      </h4>
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        {poolStatus?.total && poolStatus.completed < poolStatus.total
                          ? `${poolStatus.total - poolStatus.completed} matches still need to be played.`
                          : 'Complete all pool matches before finalizing.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleFinalize} 
              disabled={!poolStatus?.complete || finalizing}
              className="gap-2"
            >
              {finalizing && <Loader2 className="h-4 w-4 animate-spin" />}
              Finalize & Create Playoffs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FinalizePoolPlayButton;
